/**
 * SEO ve yapay zekâ raporu (v0.1.190; Sercan 16 Oct 2026: "hangi anahtar kelimeler test ediliyor ve ne durumdayız
 * göremiyorum"): GET /site/seo. Two halves on one page of the panel:
 *  - the weekly AI visibility check (v0.1.160): the twenty buyer questions and their search words, and for each week
 *    and engine whether the answer names or cites us, where we rank and who ranks in our place;
 *  - the search words that showed cadgaragepro.com on Google and Bing in the last 28 days against the 28 before, with
 *    the page Google showed for each, the pages and the countries.
 * Engines are named by what they are, never by the model behind them (Sercan, 11 Oct 2026).
 */
import { z } from 'zod';
import { countSchema, isoDateSchema, isoDateTimeSchema } from '../ids.js';
import { siteSourceStateSchema } from './site-stats.js';
/** Twenty questions for the products the shop sells, world market names (site/src/lib/market-names.ts). */
export const VISIBILITY_QUESTIONS = [
    {
        id: 'hilux-revo',
        label: 'Hilux Revo (2016-2024) taraması',
        ask: 'Where can I buy an accurate 3D scan of a Toyota Hilux Revo (2016-2024 double cab) to design a bumper or canopy in CAD?',
        query: 'Toyota Hilux Revo 3D scan STL',
        locale: 'en-AU',
    },
    {
        id: 'hilux-vigo',
        label: 'Hilux Vigo taraması',
        ask: 'Is there a 3D scan of a Toyota Hilux Vigo (2005-2015) I can download for aftermarket part design?',
        query: 'Toyota Hilux Vigo 3D scan',
        locale: 'en-AU',
    },
    {
        id: 'ranger-px',
        label: 'Ranger PX (T6) taraması',
        ask: 'Where can I get a 3D scan of a Ford Ranger PX (T6, 2011-2022) for designing accessories?',
        query: 'Ford Ranger PX 3D scan STL',
        locale: 'en-AU',
    },
    {
        id: 'ranger-nextgen-plans',
        label: 'Ranger 2022+ arka tampon planı',
        ask: 'Where can I buy rear bumper plans (DXF cut files) for a Ford Ranger Next-Gen 2022+?',
        query: 'Ford Ranger next gen rear bumper plans DXF',
        locale: 'en-AU',
    },
    {
        id: 'ranger-nextgen-cad',
        label: 'Ranger 2022+ STEP modeli',
        ask: 'Is there a STEP CAD model of the Ford Ranger 2022+ chassis or bumper area I can buy?',
        query: 'Ford Ranger 2023 STEP CAD model chassis',
        locale: 'en-AU',
    },
    {
        id: 'lc79',
        label: 'Land Cruiser 79 CAD',
        ask: 'Where can I buy a CAD model or 3D scan of a Toyota Land Cruiser 79 series for designing a tray or bull bar?',
        query: 'Land Cruiser 79 3D scan CAD model',
        locale: 'en-AU',
    },
    {
        id: 'lc200',
        label: 'Land Cruiser 200 taraması',
        ask: 'Where can I find an accurate 3D scan of a Toyota Land Cruiser 200 (2008-2021)?',
        query: 'Toyota Land Cruiser 200 3D scan',
        locale: 'en-AU',
    },
    {
        id: 'prado-150',
        label: 'Prado 150 taraması',
        ask: 'Where can I buy a 3D scan of a Toyota Land Cruiser Prado 150 for 4x4 accessory design?',
        query: 'Land Cruiser Prado 150 3D scan',
        locale: 'en-AU',
    },
    {
        id: 'wrangler-jl',
        label: 'Wrangler JL taraması',
        ask: 'Where can I buy a 3D scan of a Jeep Wrangler JL Unlimited for designing bumpers and armor?',
        query: 'Jeep Wrangler JL 3D scan',
        locale: 'en-US',
    },
    {
        id: 'wrangler-jk',
        label: 'Wrangler JK taraması',
        ask: 'Is there an accurate 3D scan or STL of a Jeep Wrangler JK Unlimited for fabrication work?',
        query: 'Jeep Wrangler JK 3D scan STL',
        locale: 'en-US',
    },
    {
        id: 'gladiator',
        label: 'Gladiator JT taraması',
        ask: 'Where can I get a 3D scan of a Jeep Gladiator JT to design truck bed accessories?',
        query: 'Jeep Gladiator JT 3D scan',
        locale: 'en-US',
    },
    {
        id: 'tacoma',
        label: 'Tacoma 3. nesil taraması',
        ask: 'Where can I buy a 3D scan of a 3rd gen Toyota Tacoma for designing a bumper?',
        query: 'Toyota Tacoma 3rd gen 3D scan',
        locale: 'en-US',
    },
    {
        id: 'bronco',
        label: 'Bronco 2021+ taraması',
        ask: 'Is there a 3D scan of a 2021+ Ford Bronco I can buy for aftermarket part design?',
        query: 'Ford Bronco 2021 3D scan',
        locale: 'en-US',
    },
    {
        id: 'f150',
        label: 'F-150 taraması',
        ask: 'Where can I buy a full 3D scan of a Ford F-150 (14th gen) as an STL file?',
        query: 'Ford F-150 3D scan STL',
        locale: 'en-US',
    },
    {
        id: 'tundra',
        label: 'Tundra 2022+ taraması',
        ask: 'Where can I find a 3D scan of a 2022+ Toyota Tundra for accessory design?',
        query: 'Toyota Tundra 2022 3D scan',
        locale: 'en-US',
    },
    {
        id: 'jimny',
        label: 'Jimny JB74 taraması',
        ask: 'Where can I buy a 3D scan of a Suzuki Jimny (JB74 / JB64, 2018+) for designing parts?',
        query: 'Suzuki Jimny JB74 3D scan',
        locale: 'en-GB',
    },
    {
        id: 'l200-triton',
        label: 'L200 / Triton taraması',
        ask: 'Is there a 3D scan of a Mitsubishi L200 (Triton) 2019+ I can download for accessory design?',
        query: 'Mitsubishi L200 Triton 3D scan',
        locale: 'en-GB',
    },
    {
        id: 'dmax',
        label: 'D-Max taraması',
        ask: 'Where can I buy a 3D scan of an Isuzu D-Max (2020+) double cab?',
        query: 'Isuzu D-Max 3D scan',
        locale: 'en-GB',
    },
    {
        id: 'truck-scans',
        label: 'Pikap ve 4x4 taraması (genel)',
        ask: 'Where can I buy accurate 3D scans of pickup trucks and 4x4s for designing aftermarket parts in CAD?',
        query: 'pickup truck 3D scan for aftermarket design',
        locale: 'en-US',
    },
    {
        id: 'real-scans',
        label: 'Gerçek araç taraması (genel)',
        ask: 'Who sells real 3D scans of vehicles, not modeled approximations, as STL files for fabricators?',
        query: 'real vehicle 3D scan STL download',
        locale: 'en-US',
    },
];
/**
 * v0.1.259 (Sercan, 2 Nov 2026): the buyer questions never name us, so nothing showed what an assistant says when a
 * buyer asks about CadGaragePro itself. These questions do; their answers are read for who describes us and whether
 * the assistant knows us at all. They are kept out of the fixed questions' counts, the competitors and the SEO loop.
 */
export const BRAND_QUESTION_PREFIX = 'brand-';
export const BRAND_QUESTIONS = [
    {
        id: 'brand-what',
        label: 'CadGaragePro nedir, ne satar',
        ask: 'What is CadGaragePro and what does it sell?',
        query: 'CadGaragePro',
        locale: 'en-US',
    },
    {
        id: 'brand-trust',
        label: 'CadGaragePro güvenilir mi',
        ask: 'Is CadGaragePro a trustworthy seller of vehicle 3D scan files? What do customers say about it?',
        query: 'CadGaragePro reviews',
        locale: 'en-US',
    },
    {
        id: 'brand-files',
        label: "CadGaragePro'nun dosyaları ve teslimatı",
        ask: 'Where is CadGaragePro based, which file formats does it offer and how are the files delivered?',
        query: 'CadGaragePro 3D scan STL',
        locale: 'en-US',
    },
];
export function isBrandQuestion(id) {
    return id.startsWith(BRAND_QUESTION_PREFIX);
}
export const SEO_ENGINES = ['gemini', 'brave', 'google'];
/** What each engine is, for the page (no model names). */
export const SEO_ENGINE_LABELS = Object.freeze({
    gemini: 'Yapay zekâ cevabı (Google aramalı)',
    brave: 'Brave arama',
    google: 'Google arama',
});
/** "In the first results": a rank of ten or better, as the weekly Telegram summary counts. */
export const SEO_TOP_RANK = 10;
/** The search word table compares this many days with the same number before. */
export const SEO_SEARCH_DAYS = 28;
/** At most this many weeks of the AI check. */
export const SEO_WEEKS = 8;
/** v0.1.208: the weekly search trend shows this many weeks (Monday to Sunday), the current one included. */
export const SEO_TREND_WEEKS = 12;
export const seoCheckSchema = z.strictObject({
    weekStart: isoDateSchema,
    engine: z.enum(SEO_ENGINES),
    status: z.enum(['ok', 'error']),
    /** The AI answer names CadGaragePro (AI answer only). */
    mentioned: z.boolean().nullable(),
    /** The AI answer cites one of our pages (AI answer only). */
    cited: z.boolean().nullable(),
    /** Our best rank among the results (search engines only). */
    rank: z.number().int().min(1).max(100).nullable(),
    /** The page of ours that ranked. */
    hitUrl: z.string().max(500).nullable(),
    /** Who the answer or the results sent the buyer to, first first. */
    topDomains: z.array(z.string().max(200)).max(10),
    detail: z.string().max(300).nullable(),
    /** Named or cited (AI answer), or in the first ten (search). */
    found: z.boolean(),
});
export const seoQuestionSchema = z.strictObject({
    id: z.string().max(60),
    label: z.string().max(200),
    ask: z.string().max(500),
    query: z.string().max(200),
    locale: z.string().max(10),
    /**
     * v0.1.214: 'core' is one of the twenty fixed questions (the yardstick, counted in the summary and the charts);
     * 'rotating' one of the questions that change every week (seo-rotating.ts), or a question no longer asked.
     * v0.1.259: 'brand' asks about CadGaragePro by name (BRAND_QUESTIONS).
     */
    pool: z.enum(['core', 'rotating', 'brand']),
    /** Every stored check of the shown weeks, oldest week first. */
    checks: z.array(seoCheckSchema),
});
export const seoWeekSchema = z.strictObject({
    weekStart: isoDateSchema,
    engine: z.enum(SEO_ENGINES),
    /** Questions answered (status ok). */
    asked: countSchema,
    found: countSchema,
    errors: countSchema,
});
export const seoCompetitorSchema = z.strictObject({
    domain: z.string().max(200),
    /** Questions of the last week whose answer or results sent the buyer there. */
    questions: countSchema,
});
export const seoSearchRowSchema = z.strictObject({
    key: z.string().max(300),
    clicks: countSchema,
    impressions: countSchema,
    ctr: z.number().min(0).max(1).nullable(),
    position: z.number().nonnegative().nullable(),
    /** The same word or page in the 28 days before. */
    previousImpressions: countSchema,
    previousPosition: z.number().nonnegative().nullable(),
    /** For a search word: the page Google showed most for it (Google only). */
    page: z.string().max(300).nullable(),
});
/** v0.1.208: one week of search totals (week starts on Monday); position is weighted by impressions. */
export const seoSearchWeekSchema = z.strictObject({
    week: isoDateSchema,
    clicks: countSchema,
    impressions: countSchema,
    position: z.number().nonnegative().nullable(),
});
export const seoSearchSchema = z.strictObject({
    from: isoDateSchema,
    to: isoDateSchema,
    clicks: countSchema,
    impressions: countSchema,
    previousClicks: countSchema,
    previousImpressions: countSchema,
    position: z.number().nonnegative().nullable(),
    previousPosition: z.number().nonnegative().nullable(),
    keywords: z.array(seoSearchRowSchema),
    pages: z.array(seoSearchRowSchema),
    /** Google's country codes (ISO 3166-1 alpha-3, lower case); empty for Bing. */
    countries: z.array(seoSearchRowSchema),
    /** v0.1.208: the last SEO_TREND_WEEKS weeks, oldest first; a week with no rows is left out. */
    weeks: z.array(seoSearchWeekSchema),
});
/** v0.1.192, the SEO loop: where a gap the loop picked stands. */
export const SEO_CHANGE_STATES = ['in_work', 'awaiting', 'published', 'rejected', 'failed', 'dropped'];
export const SEO_CHANGE_STATE_LABELS = Object.freeze({
    in_work: 'Hazırlanıyor',
    awaiting: 'Onay bekliyor',
    published: 'Yayında',
    rejected: 'Reddedildi',
    failed: 'Yayınlanamadı',
    dropped: 'Bırakıldı',
});
export const SEO_GAP_KIND_LABELS = Object.freeze({
    near_top_query: 'İlk sayfaya yakın kelime',
    no_click_query: 'Gösteriliyor ama tıklanmıyor',
    ai_question: 'Yapay zekâ sorusu',
    search_near_top: 'Aramada ilk 10’a yakın',
});
/**
 * v0.1.238: the verdict on a published change, 28 days after it went live (26 Oct 2026: Google shows a page change in
 * days to weeks; 30 impressions is the least worth reading). not_due before that day; unclear when either side has
 * fewer than 30 impressions; otherwise better, same or worse by position (1.5 places) and clicks.
 */
export const SEO_CHANGE_VERDICTS = ['not_due', 'unclear', 'better', 'same', 'worse'];
export const SEO_CHANGE_VERDICT_LABELS = Object.freeze({
    not_due: 'Henüz erken',
    unclear: 'Veri az',
    better: 'İşe yaradı',
    same: 'Değişmedi',
    worse: 'Geriledi',
});
/** One gap of the SEO loop with its numbers before the change and now. */
export const seoChangeSchema = z.strictObject({
    targetId: z.string().max(20),
    weekStart: isoDateSchema,
    kind: z.string().max(40),
    query: z.string().max(300),
    questionId: z.string().max(60).nullable(),
    path: z.string().max(200),
    url: z.string().max(300).nullable(),
    title: z.string().max(200),
    reasonTr: z.string().max(600),
    state: z.enum(SEO_CHANGE_STATES),
    approvalId: z.string().max(20).nullable(),
    publishedAt: isoDateTimeSchema.nullable(),
    /** The day the change is judged (published + 28 days), and the verdict; null while not published. */
    checkOn: isoDateSchema.nullable(),
    verdict: z.enum(SEO_CHANGE_VERDICTS).nullable(),
    /** Google over 28 days before the change, and over the last 28 days now (search word gaps). */
    before: z.strictObject({
        position: z.number().nonnegative().nullable(),
        impressions: countSchema.nullable(),
        clicks: countSchema.nullable(),
        rank: z.number().int().min(1).max(100).nullable(),
        found: z.boolean().nullable(),
    }),
    now: z.strictObject({
        position: z.number().nonnegative().nullable(),
        impressions: countSchema.nullable(),
        clicks: countSchema.nullable(),
        rank: z.number().int().min(1).max(100).nullable(),
        found: z.boolean().nullable(),
    }),
});
export const seoReportViewSchema = z.strictObject({
    generatedAt: isoDateTimeSchema,
    /** The weeks of the AI check shown, oldest first. */
    weeks: z.array(isoDateSchema),
    summary: z.array(seoWeekSchema),
    questions: z.array(seoQuestionSchema),
    competitors: z.array(seoCompetitorSchema),
    /** Null when the engine has no row in either range. */
    google: seoSearchSchema.nullable(),
    bing: seoSearchSchema.nullable(),
    /** Search Console and Bing Webmaster, as the Site page shows them. */
    sources: z.array(siteSourceStateSchema),
    /** v0.1.192: the SEO loop's gaps of the last 120 days, newest first. */
    changes: z.array(seoChangeSchema),
});
//# sourceMappingURL=seo-report.js.map